import {
  CircularProgress,
  Container,
  Divider,
  Stack,
  Typography,
  Grid,
  Paper,
} from "@mui/material";
import React, { useState } from "react";
import { themeOrange, themeBlue } from "../../utils/colorTheme";
import { useSelector } from "react-redux";
import { asyncStatus } from "../../utils/asyncStatus";
import { MdOutlineFileDownload, MdOutlinePeople, MdOutlineWork, MdOutlineStorefront } from "react-icons/md";
import CustomButton from "../../component/common/Button/Button";

const DashboardReports = () => {
  const [exportingType, setExportingType] = useState(null);

  const { get_dashboard_data_data } = useSelector((state) => state.admin_manage);

  const convertToCSV = (data, headers) => {
    if (!data || data.length === 0) return "";

    const headerRow = headers.map(h => h.label).join(",");
    const dataRows = data.map(item =>
      headers.map(h => {
        let value = h.accessor(item);
        if (value === null || value === undefined) value = "";
        if (typeof value === "string" && value.includes(",")) {
          value = `"${value}"`;
        }
        return value;
      }).join(",")
    );

    return [headerRow, ...dataRows].join("\n");
  };

  const downloadCSV = (csvContent, filename) => {
    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    const url = URL.createObjectURL(blob);
    link.setAttribute("href", url);
    link.setAttribute("download", filename);
    link.style.visibility = "hidden";
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const exportUsers = () => {
    setExportingType("users");
    const users = get_dashboard_data_data?.data?.users || [];
    const headers = [
      { label: "First Name", accessor: (item) => item.first_name },
      { label: "Last Name", accessor: (item) => item.last_name },
      { label: "Email", accessor: (item) => item.email },
      { label: "Phone", accessor: (item) => item.phone },
      { label: "Type", accessor: (item) => item.type },
      { label: "Created At", accessor: (item) => item.created_at },
    ];
    const csv = convertToCSV(users, headers);
    downloadCSV(csv, `users_export_${new Date().toISOString().split("T")[0]}.csv`);
    setTimeout(() => setExportingType(null), 1000);
  };

  const exportVendors = () => {
    setExportingType("vendors");
    const users = get_dashboard_data_data?.data?.users || [];
    const vendors = users.filter(u => u.type === "host");
    const headers = [
      { label: "First Name", accessor: (item) => item.first_name },
      { label: "Last Name", accessor: (item) => item.last_name },
      { label: "Email", accessor: (item) => item.email },
      { label: "Phone", accessor: (item) => item.phone },
      { label: "Created At", accessor: (item) => item.created_at },
    ];
    const csv = convertToCSV(vendors, headers);
    downloadCSV(csv, `vendors_export_${new Date().toISOString().split("T")[0]}.csv`);
    setTimeout(() => setExportingType(null), 1000);
  };

  const exportOrders = () => {
    setExportingType("orders");
    // This would need actual orders data from the API
    // For now, showing a placeholder message
    alert("Orders export requires orders data from the API. Add the orders endpoint to enable this feature.");
    setTimeout(() => setExportingType(null), 1000);
  };

  const reportCards = [
    {
      title: "Export Users",
      description: "Download all users data as CSV file",
      icon: <MdOutlinePeople size={40} color={themeBlue} />,
      onClick: exportUsers,
      type: "users",
      bgColor: "#e8f4fd",
    },
    {
      title: "Export Pros",
      description: "Download all pros data as CSV file",
      icon: <MdOutlineStorefront size={40} color={themeOrange} />,
      onClick: exportVendors,
      type: "vendors",
      bgColor: "#fff4e6",
    },
    {
      title: "Export Orders",
      description: "Download all orders data as CSV file",
      icon: <MdOutlineWork size={40} color="#22c55e" />,
      onClick: exportOrders,
      type: "orders",
      bgColor: "#e6f7e6",
    },
  ];

  return (
    <Stack bgcolor={"#FAFAFA"}>
      <Stack
        direction={"row"}
        alignItems={"center"}
        justifyContent={"space-between"}
        gap={1}
        my={1}
      >
        <Typography className="mainHeading">Reports</Typography>
      </Stack>
      <Divider />

      <Container
        sx={{ maxWidth: { xl: "xl", lg: "lg", md: "md", sm: "sm", xs: "xs" } }}
      >
        <Stack my={3}>
          <Typography className="subHeading" sx={{ mb: 2, color: "#666" }}>
            Export your data as CSV files for external use or backup
          </Typography>

          <Grid container spacing={3}>
            {reportCards.map((card, index) => (
              <Grid item xl={4} lg={4} md={6} sm={12} xs={12} key={index}>
                <Paper
                  elevation={0}
                  sx={{
                    p: 3,
                    borderRadius: "16px",
                    boxShadow: "0px 4px 10px 0px #00000026",
                    height: "100%",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                  }}
                >
                  <Stack>
                    <Stack
                      sx={{
                        width: "80px",
                        height: "80px",
                        borderRadius: "50%",
                        backgroundColor: card.bgColor,
                        alignItems: "center",
                        justifyContent: "center",
                        mb: 2,
                      }}
                    >
                      {card.icon}
                    </Stack>
                    <Typography
                      className="mainHeading"
                      sx={{ fontSize: "20px !important", mb: 1 }}
                    >
                      {card.title}
                    </Typography>
                    <Typography className="mainPara" sx={{ color: "#666", mb: 2 }}>
                      {card.description}
                    </Typography>
                  </Stack>

                  <Stack
                    className="globleGradientBlue"
                    sx={{ borderRadius: "10px", overflow: "hidden" }}
                  >
                    <CustomButton
                      style={{
                        backgroundColor: "transparent",
                        fontSize: "16px",
                        fontWeight: "500",
                        paddingTop: "12px",
                        paddingBottom: "12px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        gap: 8,
                      }}
                      onClick={card.onClick}
                      disabled={exportingType === card.type}
                    >
                      {exportingType === card.type ? (
                        <CircularProgress size={20} sx={{ color: "white" }} />
                      ) : (
                        <>
                          <MdOutlineFileDownload size={20} />
                          <Typography sx={{ color: "white" }}>Download CSV</Typography>
                        </>
                      )}
                    </CustomButton>
                  </Stack>
                </Paper>
              </Grid>
            ))}
          </Grid>
        </Stack>
      </Container>
    </Stack>
  );
};

export default DashboardReports;
